/**
 * COMPONENTE CONTACT - SECCIÓN DE CONTACTO
 * 
 * Componente que maneja la sección de contacto con:
 * - Formulario de contacto validado con Formik + Yup
 * - Información personal y profesional
 * - Enlaces a redes sociales
 * - Integración completa con sistema de temas e internacionalización
 * 
 * CARACTERÍSTICAS PRINCIPALES:
 * - Validación en tiempo real con mensajes traducidos
 * - Diseño responsivo con SimpleGrid
 * - Foto de perfil con indicador de estado online
 * - Integración con ThemeContext y LanguageContext
 * 
 * PATRÓN DE FORMULARIO:
 * - Formik para manejo de estado y envío
 * - Yup para esquemas de validación
 * - Chakra UI para componentes de formulario
 * - Traducciones dinámicas para errores
 */

import React, { useContext, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  VStack,
  Textarea,
  HStack,
  Text,
  Image,
  SimpleGrid,
  Link,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from "@chakra-ui/react";
import * as Yup from 'yup';
import { ThemeContext } from "../context/ThemeContext";
import { LanguageContext } from "../context/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faGithub, faTwitter } from "@fortawesome/free-brands-svg-icons";
import fotoPerfil from "../images/foto-perfil.png";

const Contact = () => {

  // Contextos para tema e idioma
  const {theme} = useContext(ThemeContext)
  const { t } = useContext(LanguageContext)

  // Estado para mostrar mensaje de éxito
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * CONFIGURACIÓN DEL FORMULARIO CON FORMIK
   * 
   * Maneja el estado del formulario, validación y envío.
   * Utiliza traducciones dinámicas para mensajes de error.
   */
  const formik = useFormik({
    // Valores iniciales del formulario
    initialValues: {
      visitName: "",  // Nombre del visitante
      email: "",      // Email de contacto
      comment: ""     // Mensaje/comentario
    },
    
    // Función de envío al webhook
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true)
      setShowSuccess(false)
      
      try {
        const response = await fetch('https://n8n.henrygc.com/webhook/email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: values.visitName,
            email: values.email,
            message: values.comment
          })
        })

        if (response.ok) {
          setShowSuccess(true)
          resetForm()
          // Ocultar el mensaje después de 5 segundos
          setTimeout(() => setShowSuccess(false), 5000)
        } else {
          console.error('Error al enviar el formulario')
        }
      } catch (error) {
        console.error('Error de conexión:', error)
      } finally {
        setIsSubmitting(false)
      }
    },

    // Esquema de validación con Yup y traducciones
    validationSchema: Yup.object({
      visitName: Yup.string().required(t('contact.required')),
      email: Yup.string().email(t('contact.invalidEmail')).required(t('contact.required')),
      comment: Yup.string().required(t('contact.required'))
    }),
  });

  return (
      <Box
        padding="5rem 2rem"
        background={theme.bg.contactos}
        transitionProperty="background" 
        transitionDuration="0.3s"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          maxWidth="1200px"
          width="100%"
          color={theme.color}
          transitionProperty="color" 
          transitionDuration="0.3s"
        >
          {/* Título principal */}
          <VStack spacing="2rem" textAlign="center" marginBottom="4rem">
            <Heading 
              as="h1" 
              id="contact"
              fontSize={["2.5rem", "3rem", "3.5rem"]}
              fontWeight="700"
            >
              {t('contact.mainTitle')}
            </Heading>
            <Text 
              fontSize={["1rem", "1.1rem", "1.2rem"]}
              opacity="0.8"
              maxWidth="600px"
            >
              {t('contact.subtitle')}
            </Text>
          </VStack>

          {/* Contenido principal */}
          <SimpleGrid columns={[1, 1, 2]} spacing="4rem" alignItems="start">
            
            {/* Sección izquierda - Información y foto */}
            <VStack spacing="2rem" alignItems="flex-start">
              
              {/* Foto de perfil */}
              <Box
                position="relative"
                alignSelf="center"
                marginBottom="1rem"
              >
                <Image
                  src={fotoPerfil}
                  alt="Henry GC"
                  borderRadius="50%"
                  width="250px"
                  height="250px"
                  objectFit="cover"
                  boxShadow="0 12px 32px rgba(0, 0, 0, 0.15)"
                />
                {/* Indicador online */}
                <Box
                  position="absolute"
                  bottom="1rem"
                  right="1rem"
                  width="20px"
                  height="20px"
                  borderRadius="50%"
                  bg="#10B981"
                  border="3px solid white"
                  boxShadow="0 0 0 2px rgba(16, 185, 129, 0.4)"
                />
              </Box>

              {/* Información de contacto */}
              <VStack spacing="1.5rem" alignItems="flex-start" width="100%">
                <Heading as="h3" fontSize="1.5rem" marginBottom="0.5rem">
                  {t('contact.title')}
                </Heading>
                
                <HStack spacing="1rem">
                  <Box
                    width="40px"
                    height="40px"
                    borderRadius="50%"
                    bg={theme.bg.landing === "#fff" ? "#F7FAFC" : "#2D3748"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FontAwesomeIcon icon={faEnvelope} color={theme.color} />
                  </Box>
                  <VStack spacing="0" alignItems="flex-start">
                    <Text fontSize="0.9rem" opacity="0.7">Email</Text>
                    <Text fontSize="1rem" fontWeight="500">contacto@henrygc.com</Text>
                  </VStack>
                </HStack>

                <HStack spacing="1rem">
                  <Box
                    width="40px"
                    height="40px"
                    borderRadius="50%"
                    bg={theme.bg.landing === "#fff" ? "#F7FAFC" : "#2D3748"}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} color={theme.color} />
                  </Box>
                  <VStack spacing="0" alignItems="flex-start">
                    <Text fontSize="0.9rem" opacity="0.7">Ubicación</Text>
                    <Text fontSize="1rem" fontWeight="500">Esmeraldas, Ecuador</Text>
                  </VStack>
                </HStack>
              </VStack>

              {/* Redes sociales */}
              <Box>
                <Text fontSize="1rem" fontWeight="600" marginBottom="1rem">
                  {t('contact.followMe')}
                </Text>
                <HStack spacing="1rem">
                  <Link href="#" isExternal>
                    <Box
                      width="45px"
                      height="45px"
                      borderRadius="50%"
                      bg={theme.bg.landing === "#fff" ? "#F7FAFC" : "#2D3748"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                      }}
                    >
                      <FontAwesomeIcon icon={faLinkedin} color="#0077B5" size="lg" />
                    </Box>
                  </Link>
                  <Link href="#" isExternal>
                    <Box
                      width="45px"
                      height="45px"
                      borderRadius="50%"
                      bg={theme.bg.landing === "#fff" ? "#F7FAFC" : "#2D3748"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                      }}
                    >
                      <FontAwesomeIcon icon={faGithub} color={theme.color} size="lg" />
                    </Box>
                  </Link>
                  <Link href="#" isExternal>
                    <Box
                      width="45px"
                      height="45px"
                      borderRadius="50%"
                      bg={theme.bg.landing === "#fff" ? "#F7FAFC" : "#2D3748"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                      }}
                    >
                      <FontAwesomeIcon icon={faTwitter} color="#1DA1F2" size="lg" />
                    </Box>
                  </Link>
                </HStack>
              </Box>
            </VStack>

            {/* Sección derecha - Formulario */}
            <Box
              bg={theme.bg.card}
              padding="2.5rem"
              borderRadius="1.5rem"
              border={`1px solid ${theme.borderColor}`}
              boxShadow="0 4px 16px rgba(0, 0, 0, 0.08)"
            >
              <VStack spacing="1.5rem" alignItems="flex-start" marginBottom="2rem">
                <Heading as="h3" fontSize="1.8rem">
                  {t('contact.title')}
                </Heading>
                <Text opacity="0.7">
                  {t('contact.formInstructions')}
                </Text>
              </VStack>

              {/* Alert de éxito */}
              {showSuccess && (
                <Alert
                  status="success"
                  borderRadius="0.8rem"
                  marginBottom="1.5rem"
                  bg="#F0FDF4"
                  border="1px solid #10B981"
                >
                  <AlertIcon color="#10B981" />
                  <Box>
                    <AlertTitle color="#065F46" fontSize="0.9rem" fontWeight="600">
                      {t('contact.successMessage')}
                    </AlertTitle>
                    <AlertDescription color="#047857" fontSize="0.8rem">
                      {t('contact.successDescription')}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              <form onSubmit={formik.handleSubmit}>
                <VStack spacing="1.5rem">
                  <FormControl isInvalid={formik.touched.visitName && formik.errors.visitName}>
                    <FormLabel htmlFor="visitName" fontSize="0.9rem" fontWeight="600">
                      {t('contact.name')}
                    </FormLabel>
                    <Input
                      id="visitName"
                      name="visitName"
                      placeholder={t('contact.name')}
                      bg={theme.bg.landing}
                      border={`1px solid ${theme.borderColor}`}
                      borderRadius="0.8rem"
                      padding="1rem"
                      fontSize="1rem"
                      _focus={{
                        borderColor: theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4",
                        boxShadow: `0 0 0 1px ${theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4"}`
                      }}
                      {...formik.getFieldProps("visitName")}
                    />
                    <FormErrorMessage>{formik.errors.visitName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formik.touched.email && formik.errors.email}>
                    <FormLabel htmlFor="email" fontSize="0.9rem" fontWeight="600">
                      {t('contact.email')}
                    </FormLabel>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t('contact.email')}
                      bg={theme.bg.landing}
                      border={`1px solid ${theme.borderColor}`}
                      borderRadius="0.8rem"
                      padding="1rem"
                      fontSize="1rem"
                      _focus={{
                        borderColor: theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4",
                        boxShadow: `0 0 0 1px ${theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4"}`
                      }}
                      {...formik.getFieldProps("email")}
                    />
                    <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={formik.touched.comment && formik.errors.comment}>
                    <FormLabel htmlFor="comment" fontSize="0.9rem" fontWeight="600">
                      {t('contact.message')}
                    </FormLabel>
                    <Textarea
                      id="comment"
                      name="comment"
                      placeholder={t('contact.message')}
                      height="120px"
                      bg={theme.bg.landing}
                      border={`1px solid ${theme.borderColor}`}
                      borderRadius="0.8rem"
                      padding="1rem"
                      fontSize="1rem"
                      resize="vertical"
                      _focus={{
                        borderColor: theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4",
                        boxShadow: `0 0 0 1px ${theme.bg.landing === "#fff" ? "#2B6CB0" : "#90CDF4"}`
                      }}
                      {...formik.getFieldProps("comment")}
                    />
                    <FormErrorMessage>{formik.errors.comment}</FormErrorMessage>
                  </FormControl>

                  <Button 
                    type="submit" 
                    width="full"
                    bg={theme.bg.landing === "#fff" ? "#2B6CB0" : "#4299E1"}
                    color="white"
                    borderRadius="0.8rem"
                    padding="1.2rem"
                    fontSize="1rem"
                    fontWeight="600"
                    isLoading={isSubmitting}
                    loadingText="Enviando..."
                    _hover={{
                      bg: theme.bg.landing === "#fff" ? "#2C5282" : "#3182CE",
                      transform: "translateY(-1px)"
                    }}
                    _active={{
                      transform: "translateY(0)"
                    }}
                    transition="all 0.2s ease"
                    _disabled={{
                      opacity: 0.6,
                      cursor: "not-allowed"
                    }}
                  >
                    {t('contact.send')}
                  </Button>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>
  );
};

export default Contact;
